import { AppState } from './AppStateContext';

declare global {
  interface Window {
    pageDataTracker?: {
      trackEvent: (event: string, data: any) => {}
      trackPageLoad: (data:any) => {}
    };
    pageData?: any;
  }
}

interface ContentDetails {
  format: string,
  id: string,
  type: string,
  title: string,
  detail: string
}

interface SearchDetails {
  resultsPerPage: number,
  type: string,
  resultsPosition: number,
  criteria: string,
  facets: {
    name: string,
    values: string[],
  }[],
}

interface PageDetails {
  environment: string,
  language: string,
  businessUnit: string,
  noTracking: boolean,
  productName: string,
  name: string,
  analyticsPagename: string, // Important!
  type: string,
}

interface VisitorDetails {
  accessType: string,
  ipAddress: string,
}

interface JournalDetails {
  name: string,
  code?: string,
}

export enum AnalyticsEvents {
  InitState = 'InitState',
  InitPageLoad = 'pageLoad',
  PageRendered = 'newPage',
  LinkClick = 'searchResultsClick',
  FiltersUpdated = 'searchResultsUpdated',
}

export interface AnalyticsData {
  page: PageDetails,
  visitor: VisitorDetails,
  content: ContentDetails[],
  search: SearchDetails,
  journal: JournalDetails,
}

export class AnalyticsTracking {
  static getInitialAnalyticsData(state: AppState): AnalyticsData {
    /*
      Adobe analytics is bound to window on page init,
      if not there for any reason, ensure graceful page load anyway
    */

    return {
      page: {
        environment: state.appEnv,
        language: state.aaLanguage,
        businessUnit: state.aaBusinessUnit,
        noTracking: state.appEnv==='local',
        productName: state.aaProductName,
        name: `${state.aaProductName}:analyze_manuscript_references`,
        analyticsPagename: `${state.aaProductName}:analyze_manuscript_references`, // Important!
        type: state.aaPageType,
      },
      visitor: {
        accessType: state.aaAnonymousVisitor,
        ipAddress:"n/a"
      },
      content: [],
      journal: {
        name: '',
      },
      search: {
        resultsPerPage: 0,
        type: '',
        resultsPosition: 0,
        criteria: '',
        facets: [{
          name: 'documentType',
          values: []
        },
        {
          name: 'author',
          values: []
        },
        {
          name: 'publication',
          values: []
        },
        {
          name: 'yearPublished',
          values: []
        }],
      },
    }
  }

  static trackEvent(event: string, data: any) {
    data.page.loadTimestamp = `${new Date().getTime()}`
    console.log("tracking", event, ': ', data);
    window?.pageDataTracker?.trackEvent(event, data);
  }

  static canTrack(state: AppState) {
    return state.appEnv !== 'local';
  }

  static analyticsReducer(state: AnalyticsData, action: { type: AnalyticsEvents, payload: any }): AnalyticsData {
    if (state.page.noTracking) {
      console.log("skipping", action.type, ': ', action.payload);
    } else {
      switch (action.type) {
        case AnalyticsEvents.InitState:
          const {appState} = action.payload;
          state = AnalyticsTracking.getInitialAnalyticsData(appState);
          break;
        case AnalyticsEvents.InitPageLoad:
          state = AnalyticsTracking.getInitialAnalyticsData(action.payload)
          const { resultsPerPage, documentId, revision, journalCode, journal, title } = action.payload;
          state.search.resultsPerPage = resultsPerPage;
          state.search.type = `${state.page.productName}:manuscript_references`;
          const journalCodeUpper = journalCode.toUpperCase();
          const productNameLower = state.page.productName.toLowerCase();
          state.content = [{
            id: `${productNameLower}:manuscript:id:${journalCodeUpper}-${documentId}.${revision}`,
            type: `${state.page.productName}:manuscript`,
            title: title,
            detail: "",
            format: ""
          }]
          state.journal = {
            name: journal,
            code: journalCodeUpper
          }
          AnalyticsTracking.trackEvent(AnalyticsEvents.InitPageLoad, state);
          break;
        case AnalyticsEvents.PageRendered:
          const { pagename } = action.payload;
          const page = `${state.page.productName}:${pagename}`;
          state.page.name = page;
          state.page.analyticsPagename = page;
          AnalyticsTracking.trackEvent(AnalyticsEvents.PageRendered, state);
          break;
        case AnalyticsEvents.LinkClick:
          const { refType, refTitle, itemId, itemLocation } = action.payload;
          state.content = [{
            format: "MIME-HTML",
            id: `${state.page.productName}:itemId:${itemId}`,
            type: `${state.page.productName}:${refType}`,
            title: refTitle,
            detail: `${state.page.productName}:clickLocation:${itemLocation}`,
          }]
          AnalyticsTracking.trackEvent(AnalyticsEvents.LinkClick, state);
          break;
        case AnalyticsEvents.FiltersUpdated:
          const filters: { id: string, values: string[] }[] = action.payload.filters;
          state.search.facets = filters.map(filter => {
            return {
              name: filter.id,
              values: filter.values
            }
          })
          AnalyticsTracking.trackEvent(AnalyticsEvents.FiltersUpdated, state);
          break;
        default:
          break;
      }
    }
    window?.pageData?.push(state);
    return state
  }
}


